/**
 * Enhanced AI/ML-based prediction model for sunrise/sunset quality
 * Shared between Lambda functions
 *
 * VERSION 2.1 - Simplified 4-Factor Model
 * - Cloud Altitude Distribution: 35% (with rangeBellCurve for 30-60% optimal)
 * - AOD Scattering: 25% (Koschmieder equation fallback when no direct AOD)
 * - Atmospheric Clarity: 25% (visibility 60% + humidity 40%)
 * - Atmospheric Stability: 15% (pressure-based)
 * - Bonus factors: Cloud Type (Cirrus) +5, Ozone Seasonal ±5
 * - Updated thresholds: VIVID 90+, GREAT 70+, GOOD 50+, FAIR 35+
 *
 * VERSION 2.0 - Enhanced with atmospheric physics
 * - Aerosol Optical Depth (AOD) integration for Mie scattering effects
 * - Enhanced cloud type classification
 * - Ozone absorption layer
 * - Improved weight calibration based on atmospheric optics research
 *
 * Based on atmospheric optics research, Rayleigh/Mie scattering physics,
 * and empirical data from SunsetWX, NOAA, and professional photographers
 */

export type QualityCategory = 'POOR' | 'FAIR' | 'GOOD' | 'GREAT' | 'VIVID';
export type AlgorithmVersion = 'v1.0' | 'v2.0' | 'v2.1' | 'v2.2';

export interface PredictionInput {
  cloudCoverTotal: number; // 0-100%
  cloudCoverLow: number; // 0-100% (0-2000m altitude)
  cloudCoverMid: number; // 0-100% (2000-6000m altitude)
  cloudCoverHigh: number; // 0-100% (>6000m altitude)
  humidity: number; // 0-100%
  visibility: number; // meters
  pressure: number; // hPa
  // V2.0 additions
  aod550nm?: number; // Aerosol Optical Depth at 550nm (optional)
  pm25?: number; // PM2.5 particulate matter μg/m³ (optional)
  pm10?: number; // PM10 particulate matter μg/m³ (optional)
  ozoneDU?: number; // Ozone in Dobson Units (optional)
  /**
   * Precipitation in the prior ~6 hours (mm). When provided and ≥ 0.5 mm,
   * V2.2 may apply a post-rain washed-atmosphere bonus. Without this field,
   * the bonus is never applied (avoids false positives from clear-air heuristics).
   */
  precipitationLast6hMm?: number;
}

export interface FactorExplanation {
  name: string;
  score: number; // 0-100
  weight: number; // Percentage weight in calculation
  contribution: number; // Actual points contributed to final score
  interpretation: string; // Human-readable interpretation
  details: string; // Technical details
}

export interface QualityPrediction {
  score: number; // 0-100
  category: QualityCategory;
  factors: {
    cloudAltitudeScore: number;
    cloudCoverScore: number;
    humidityScore: number;
    visibilityScore: number;
    pressureScore: number;
    // V2.0 additions
    aodScore?: number;
    ozoneScore?: number;
    cloudTypeBonus?: number;
  };
  explanation: string;
  algorithmVersion: AlgorithmVersion;
  factorBreakdown?: FactorExplanation[]; // V2.0+ detailed breakdown
}

/**
 * Bell curve function for optimal cloud cover calculation
 */
function bellCurve(value: number, center: number, width: number): number {
  const deviation = Math.abs(value - center) / width;
  return Math.max(0, 100 * Math.exp(-deviation * deviation));
}

/**
 * Range bell curve function for V2.1 algorithm
 * Returns 100 within optimal range, gaussian falloff outside
 *
 * @param value - Current value to evaluate
 * @param optimalMin - Lower bound of optimal range (returns 100)
 * @param optimalMax - Upper bound of optimal range (returns 100)
 * @param falloffWidth - Width of gaussian falloff outside optimal range
 * @returns Score from 0-100
 */
function rangeBellCurve(
  value: number,
  optimalMin: number,
  optimalMax: number,
  falloffWidth: number
): number {
  if (value >= optimalMin && value <= optimalMax) {
    return 100;
  } else if (value < optimalMin) {
    return Math.max(0, 100 * Math.exp(-Math.pow((optimalMin - value) / falloffWidth, 2)));
  } else {
    return Math.max(0, 100 * Math.exp(-Math.pow((value - optimalMax) / falloffWidth, 2)));
  }
}

/**
 * Estimate AOD from visibility using Koschmieder equation
 * Used as fallback when direct AOD measurements are unavailable
 *
 * @param visibility - Visibility in meters
 * @param humidity - Relative humidity percentage
 * @returns Estimated AOD value (clamped between 0.01 and 2.0)
 */
function estimateAODFromVisibility(visibility: number, humidity: number): number {
  const visibilityKm = visibility / 1000;
  // Koschmieder equation: AOD ≈ 3.912 / visibility(km)
  let aod = 3.912 / visibilityKm;
  // Humidity adjustment: high humidity increases effective AOD
  if (humidity > 70) {
    aod *= 1 + (humidity - 70) / 100;
  }
  return Math.min(2.0, Math.max(0.01, aod));
}

/**
 * Gaussian curve for pressure optimization
 */
function gaussianCurve(value: number, center: number, width: number): number {
  const deviation = Math.abs(value - center) / width;
  return Math.max(0, 100 * Math.exp(-0.5 * deviation * deviation));
}

/**
 * Calculate AOD (Aerosol Optical Depth) score
 *
 * Research shows optimal AOD range is 0.1-0.3 for sunset quality:
 * - Too clean (<0.05): Minimal Mie scattering, less vibrant colors
 * - Optimal (0.1-0.3): Enhanced Mie scattering, spectacular reds/oranges
 * - Too hazy (>0.5): Excessive scattering, dulled/muted colors
 *
 * @param aod550nm - Aerosol Optical Depth at 550nm wavelength
 * @returns Score from 0-100
 */
function calculateAODScore(aod550nm: number): number {
  if (aod550nm >= 0.1 && aod550nm <= 0.3) {
    // Optimal range - maximum score
    return 100;
  } else if (aod550nm < 0.1) {
    // Too clean - linear scale from 0.0 to 0.1
    // Very clean air (AOD < 0.05) scores around 40
    return 40 + (aod550nm / 0.1) * 60;
  } else if (aod550nm <= 0.5) {
    // Moderately hazy - decreasing linearly
    return 100 - ((aod550nm - 0.3) / 0.2) * 50;
  } else {
    // Very hazy - sharply declining
    const excess = aod550nm - 0.5;
    return Math.max(10, 50 - (excess / 0.5) * 40);
  }
}

/**
 * Calculate AOD score for V2.2 algorithm with tighter sweet spot
 *
 * V2.2 uses refined empirical range: 0.12-0.25 (peaks at 0.18)
 * - Too clean (<0.08): Minimal Mie scattering
 * - Optimal (0.12-0.25): Peak scattering at 0.18
 * - Too hazy (>0.4): Excessive scattering
 *
 * @param aod550nm - Aerosol Optical Depth at 550nm wavelength
 * @returns Score from 0-100
 */
function calculateAODScoreV2_2(aod550nm: number): number {
  // Peak at 0.18 (empirical optimum)
  const optimal = 0.18;
  
  if (aod550nm >= 0.12 && aod550nm <= 0.25) {
    // Within optimal range - gaussian peak at 0.18
    const deviation = Math.abs(aod550nm - optimal);
    return Math.max(95, 100 - (deviation / 0.065) * 5);
  } else if (aod550nm < 0.12) {
    // Too clean - gentler falloff below optimal
    if (aod550nm < 0.05) {
      return 30;
    }
    return 30 + ((aod550nm - 0.05) / 0.07) * 65;
  } else if (aod550nm <= 0.4) {
    // Moderately hazy - linear decrease
    return 95 - ((aod550nm - 0.25) / 0.15) * 50;
  } else {
    // Very hazy - sharp decline
    const excess = aod550nm - 0.4;
    return Math.max(5, 45 - (excess / 0.6) * 40);
  }
}

/**
 * Calculate PM2.5 score (particulate matter)
 *
 * PM2.5 affects visibility and color quality:
 * - Good (0-35): Excellent conditions
 * - Moderate (35-75): Good to fair
 * - Unhealthy (75+): Poor conditions
 *
 * @param pm25 - PM2.5 concentration in μg/m³
 * @returns Score from 0-100
 */
function calculatePM25Score(pm25: number): number {
  if (pm25 <= 12) {
    return 100; // Excellent air quality
  } else if (pm25 <= 35) {
    return 100 - ((pm25 - 12) / 23) * 15; // Good
  } else if (pm25 <= 55) {
    return 85 - ((pm25 - 35) / 20) * 35; // Moderate
  } else if (pm25 <= 150) {
    return 50 - ((pm25 - 55) / 95) * 30; // Unhealthy
  } else {
    return Math.max(10, 20 - ((pm25 - 150) / 100) * 10); // Very unhealthy
  }
}

/**
 * Calculate ozone absorption score
 *
 * Stratospheric ozone affects color balance by filtering UV and some visible light
 * Optimal range varies by season
 *
 * @param ozoneDU - Total column ozone in Dobson Units
 * @param month - Month (0-11) for seasonal adjustment
 * @returns Score from 0-100
 */
function calculateOzoneScore(ozoneDU: number, month: number): number {
  // Determine season (Northern Hemisphere)
  let season: 'winter' | 'spring' | 'summer' | 'fall';
  if (month === 11 || month <= 1) season = 'winter';
  else if (month >= 2 && month <= 4) season = 'spring';
  else if (month >= 5 && month <= 7) season = 'summer';
  else season = 'fall';

  // Seasonal optimal ranges
  const optimalRanges = {
    winter: { min: 300, max: 400 },
    spring: { min: 320, max: 420 },
    summer: { min: 280, max: 380 },
    fall: { min: 300, max: 400 },
  };

  const range = optimalRanges[season];

  if (ozoneDU >= range.min && ozoneDU <= range.max) {
    return 100; // Optimal
  } else if (ozoneDU < range.min) {
    // Low ozone - less absorption, different color balance
    const deficit = range.min - ozoneDU;
    return Math.max(60, 100 - (deficit / 80) * 40);
  } else {
    // High ozone - excessive absorption
    const excess = ozoneDU - range.max;
    return Math.max(70, 100 - (excess / 50) * 30);
  }
}

/**
 * Generate detailed factor breakdown for user understanding
 *
 * Converts raw factor scores into user-friendly explanations with
 * interpretations and technical details for each scoring component.
 *
 * @param factors - Factor scores from prediction calculation
 * @param input - Original prediction input for raw values
 * @param algorithmVersion - Which algorithm was used
 * @returns Array of FactorExplanation objects ordered by contribution
 */
function generateFactorBreakdown(
  factors: QualityPrediction['factors'],
  input: PredictionInput,
  algorithmVersion: AlgorithmVersion
): FactorExplanation[] {
  const breakdown: FactorExplanation[] = [];

  // Determine weights based on algorithm version
  const weights = algorithmVersion === 'v2.0'
    ? {
        cloudAltitude: 30,
        aod: 20,
        cloudCover: 20,
        humidity: 7.5,
        visibility: 7.5,
        cloudType: 10,
        pressure: 5,
      }
    : {
        cloudAltitude: 35,
        cloudCover: 25,
        humidity: 10,
        visibility: 15,
        cloudType: 5,
        pressure: 10,
      };

  // 1. CLOUD ALTITUDE
  const cloudAltitudeScore = factors.cloudAltitudeScore;
  const cloudAltitudeWeight = weights.cloudAltitude;
  const cloudAltitudeContribution = (cloudAltitudeScore * cloudAltitudeWeight) / 100;

  let cloudAltitudeInterpretation: string;
  if (cloudAltitudeScore >= 90) {
    cloudAltitudeInterpretation = 'Excellent high cloud coverage';
  } else if (cloudAltitudeScore >= 70) {
    cloudAltitudeInterpretation = 'Good mid-level clouds';
  } else if (cloudAltitudeScore >= 50) {
    cloudAltitudeInterpretation = 'Moderate cloud altitude';
  } else if (cloudAltitudeScore >= 30) {
    cloudAltitudeInterpretation = 'Low clouds limiting color';
  } else {
    cloudAltitudeInterpretation = 'Poor cloud positioning';
  }

  breakdown.push({
    name: 'Cloud Altitude',
    score: cloudAltitudeScore,
    weight: cloudAltitudeWeight,
    contribution: cloudAltitudeContribution,
    interpretation: cloudAltitudeInterpretation,
    details: `High: ${input.cloudCoverHigh}%, Mid: ${input.cloudCoverMid}%, Low: ${input.cloudCoverLow}%`,
  });

  // 2. AEROSOL OPTICAL DEPTH (V2.0 only)
  if (algorithmVersion === 'v2.0' && factors.aodScore !== undefined) {
    const aodScore = factors.aodScore;
    const aodWeight = weights.aod!; // V2.0 always has aod weight
    const aodContribution = (aodScore * aodWeight) / 100;

    let aodInterpretation: string;
    let aodDetails: string;

    if (input.aod550nm !== undefined && input.aod550nm !== null) {
      const aodValue = input.aod550nm;
      if (aodValue >= 0.1 && aodValue <= 0.3) {
        aodInterpretation = 'Optimal particle concentration for vivid colors';
      } else if (aodValue < 0.05) {
        aodInterpretation = 'Very clean air - may reduce color intensity';
      } else if (aodValue >= 0.05 && aodValue < 0.1) {
        aodInterpretation = 'Clean air with good scattering potential';
      } else if (aodValue > 0.3 && aodValue <= 0.5) {
        aodInterpretation = 'Moderate haze - good scattering';
      } else {
        aodInterpretation = 'High aerosol concentration dulling colors';
      }
      aodDetails = `AOD: ${aodValue.toFixed(3)} at 550nm (ideal: 0.1-0.3)`;
    } else if (input.pm25 !== undefined) {
      aodInterpretation = 'Estimated from particulate matter data';
      aodDetails = `PM2.5: ${input.pm25.toFixed(1)} μg/m³`;
    } else {
      aodInterpretation = 'No AOD data available - neutral score applied';
      aodDetails = 'Default score: 50/100';
    }

    breakdown.push({
      name: 'Aerosol Optical Depth',
      score: aodScore,
      weight: aodWeight,
      contribution: aodContribution,
      interpretation: aodInterpretation,
      details: aodDetails,
    });
  }

  // 3. CLOUD COVER
  const cloudCoverScore = factors.cloudCoverScore;
  const cloudCoverWeight = weights.cloudCover;
  const cloudCoverContribution = (cloudCoverScore * cloudCoverWeight) / 100;

  let cloudCoverInterpretation: string;
  const totalCloud = input.cloudCoverTotal;
  if (totalCloud >= 40 && totalCloud <= 60) {
    cloudCoverInterpretation = 'Ideal cloud coverage for reflection';
  } else if ((totalCloud >= 30 && totalCloud < 40) || (totalCloud > 60 && totalCloud <= 70)) {
    cloudCoverInterpretation = 'Good coverage for color development';
  } else if (totalCloud < 30) {
    cloudCoverInterpretation = 'Limited clouds for color';
  } else {
    cloudCoverInterpretation = 'Heavy overcast may dull colors';
  }

  breakdown.push({
    name: 'Cloud Cover',
    score: cloudCoverScore,
    weight: cloudCoverWeight,
    contribution: cloudCoverContribution,
    interpretation: cloudCoverInterpretation,
    details: `Total cloud cover: ${totalCloud}%`,
  });

  // 4. ATMOSPHERIC CLARITY (Visibility + Humidity combined)
  const visibilityScore = factors.visibilityScore;
  const humidityScore = factors.humidityScore;
  const combinedScore = Math.round((visibilityScore + humidityScore) / 2);
  const combinedWeight = weights.visibility + weights.humidity;
  const combinedContribution = (visibilityScore * weights.visibility) / 100 + (humidityScore * weights.humidity) / 100;

  let clarityInterpretation: string;
  if (visibilityScore >= 85 && humidityScore >= 85) {
    clarityInterpretation = 'Crystal clear atmospheric conditions';
  } else if (visibilityScore >= 70 && humidityScore >= 70) {
    clarityInterpretation = 'Good atmospheric clarity';
  } else if (visibilityScore < 50 || humidityScore < 50) {
    clarityInterpretation = 'Poor atmospheric clarity';
  } else {
    clarityInterpretation = 'Moderate atmospheric conditions';
  }

  const visibilityKm = input.visibility / 1000;
  breakdown.push({
    name: 'Atmospheric Clarity',
    score: combinedScore,
    weight: combinedWeight,
    contribution: combinedContribution,
    interpretation: clarityInterpretation,
    details: `Visibility: ${visibilityKm.toFixed(1)}km, Humidity: ${input.humidity}%`,
  });

  // 5. CLOUD TYPE QUALITY
  if (factors.cloudTypeBonus !== undefined) {
    const cloudTypeScore = factors.cloudTypeBonus;
    const cloudTypeWeight = weights.cloudType;
    const cloudTypeContribution = (cloudTypeScore * cloudTypeWeight) / 100;

    let cloudTypeInterpretation: string;
    if (cloudTypeScore >= 90) {
      cloudTypeInterpretation = 'Perfect cirrus structure for color';
    } else if (cloudTypeScore >= 70) {
      cloudTypeInterpretation = 'Good cloud formations';
    } else if (cloudTypeScore >= 50) {
      cloudTypeInterpretation = 'Average cloud types';
    } else {
      cloudTypeInterpretation = 'Suboptimal cloud types';
    }

    let cloudTypeDetails: string;
    if (input.cloudCoverHigh > 40) {
      cloudTypeDetails = 'High cirrus with minimal low clouds';
    } else if (input.cloudCoverMid > 30) {
      cloudTypeDetails = 'Mid-level altocumulus formations';
    } else if (input.cloudCoverLow > 60) {
      cloudTypeDetails = 'Low stratocumulus blocking horizon';
    } else {
      cloudTypeDetails = 'Mixed cloud types';
    }

    breakdown.push({
      name: 'Cloud Type',
      score: cloudTypeScore,
      weight: cloudTypeWeight,
      contribution: cloudTypeContribution,
      interpretation: cloudTypeInterpretation,
      details: cloudTypeDetails,
    });
  }

  // 6. PRESSURE STABILITY
  const pressureScore = factors.pressureScore;
  const pressureWeight = weights.pressure;
  const pressureContribution = (pressureScore * pressureWeight) / 100;

  let pressureInterpretation: string;
  if (input.pressure >= 1010 && input.pressure <= 1020) {
    pressureInterpretation = 'Stable atmospheric conditions';
  } else if (input.pressure < 1000) {
    pressureInterpretation = 'Low pressure - potentially unstable';
  } else if (input.pressure > 1025) {
    pressureInterpretation = 'High pressure - very stable';
  } else {
    pressureInterpretation = 'Moderate atmospheric stability';
  }

  breakdown.push({
    name: 'Pressure Stability',
    score: pressureScore,
    weight: pressureWeight,
    contribution: pressureContribution,
    interpretation: pressureInterpretation,
    details: `Pressure: ${input.pressure} hPa`,
  });

  // 7. OZONE (Optional bonus - V2.0 only)
  if (algorithmVersion === 'v2.0' && factors.ozoneScore !== undefined && input.ozoneDU !== undefined) {
    const ozoneScore = factors.ozoneScore;
    // Ozone is a bonus factor (±5 points max), not a weighted percentage
    const ozoneAdjustment = ((ozoneScore - 85) / 15) * 5;

    let ozoneInterpretation: string;
    if (ozoneScore >= 90) {
      ozoneInterpretation = 'Optimal ozone levels for color balance';
    } else if (ozoneScore >= 70) {
      ozoneInterpretation = 'Good ozone levels';
    } else {
      ozoneInterpretation = 'Suboptimal ozone affecting color balance';
    }

    breakdown.push({
      name: 'Ozone Layer',
      score: ozoneScore,
      weight: 0, // Bonus factor, not percentage-weighted
      contribution: ozoneAdjustment,
      interpretation: ozoneInterpretation,
      details: `Ozone: ${input.ozoneDU} DU (seasonal adjustment applied)`,
    });
  }

  // Sort by contribution (descending)
  breakdown.sort((a, b) => b.contribution - a.contribution);

  return breakdown;
}

/**
 * Calculate quality score based on atmospheric conditions - VERSION 1.0
 *
 * Weight Distribution:
 * - Cloud Altitude Distribution: 35%
 * - Total Cloud Cover Optimality: 25%
 * - Humidity: 10%
 * - Visibility: 15%
 * - Pressure: 10%
 * - Cloud Type Bonus: 5%
 */
export function calculateQualityScoreV1(input: PredictionInput): QualityPrediction {
  const { cloudCoverTotal, cloudCoverLow, cloudCoverMid, cloudCoverHigh, humidity, visibility, pressure } = input;

  // Cloud Altitude Distribution (35% weight)
  const highCloudContribution = cloudCoverHigh * 1.0;
  const midCloudContribution = cloudCoverMid * 0.75;
  const lowCloudPenalty = cloudCoverLow * 0.5;

  let cloudAltitudeScore = highCloudContribution + midCloudContribution - lowCloudPenalty;
  cloudAltitudeScore = Math.max(0, Math.min(100, ((cloudAltitudeScore + 50) / 1.5) * 100));

  // Total Cloud Cover Optimality (25% weight)
  const cloudCoverScore = bellCurve(cloudCoverTotal, 50, 20);

  // Humidity Score (10% weight)
  let humidityScore = 0;
  if (humidity < 30) {
    humidityScore = 100;
  } else if (humidity < 50) {
    humidityScore = 85;
  } else if (humidity < 70) {
    humidityScore = 60;
  } else if (humidity < 85) {
    humidityScore = 30;
  } else {
    humidityScore = 10;
  }

  // Visibility Score (15% weight)
  let visibilityScore = 0;
  const visibilityKm = visibility / 1000;

  if (visibilityKm > 20) {
    visibilityScore = 100;
  } else if (visibilityKm > 15) {
    visibilityScore = 90;
  } else if (visibilityKm > 10) {
    visibilityScore = 75;
  } else if (visibilityKm > 5) {
    visibilityScore = 50;
  } else if (visibilityKm > 2) {
    visibilityScore = 25;
  } else {
    visibilityScore = 10;
  }

  // Pressure Score (10% weight)
  const pressureScore = gaussianCurve(pressure, 1015, 15);

  // Cloud Type Bonus (5% weight)
  let cloudTypeBonus = 0;

  if (cloudCoverHigh > 40 && cloudCoverLow < 30) {
    cloudTypeBonus = 100;
  } else if (cloudCoverMid > 30 && cloudCoverLow < 40) {
    cloudTypeBonus = 85;
  } else if (cloudCoverHigh + cloudCoverMid > 40 && cloudCoverLow < 50) {
    cloudTypeBonus = 70;
  } else if (cloudCoverLow > 60) {
    cloudTypeBonus = 10;
  } else {
    cloudTypeBonus = 50;
  }

  // Final Score
  const finalScore = Math.round(
    cloudAltitudeScore * 0.35 +
      cloudCoverScore * 0.25 +
      humidityScore * 0.1 +
      visibilityScore * 0.15 +
      pressureScore * 0.1 +
      cloudTypeBonus * 0.05
  );

  // Category Determination
  let category: QualityCategory;
  if (finalScore >= 90) {
    category = 'VIVID';
  } else if (finalScore >= 75) {
    category = 'GREAT';
  } else if (finalScore >= 60) {
    category = 'GOOD';
  } else if (finalScore >= 40) {
    category = 'FAIR';
  } else {
    category = 'POOR';
  }

  // Explanation
  const explanation = generateExplanation(category, cloudCoverHigh, cloudCoverMid, cloudCoverLow, cloudCoverTotal, humidity, visibilityKm);

  return {
    score: finalScore,
    category,
    factors: {
      cloudAltitudeScore: Math.round(cloudAltitudeScore),
      cloudCoverScore: Math.round(cloudCoverScore),
      humidityScore: Math.round(humidityScore),
      visibilityScore: Math.round(visibilityScore),
      pressureScore: Math.round(pressureScore),
    },
    explanation,
    algorithmVersion: 'v1.0',
  };
}

/**
 * Calculate quality score based on atmospheric conditions - VERSION 2.0
 *
 * Enhanced with aerosol optical depth, improved cloud metrics, and ozone absorption
 *
 * Weight Distribution:
 * - Cloud Altitude Distribution: 30% (reduced from 35%)
 * - Aerosol Optical Depth (AOD): 20% (NEW)
 * - Total Cloud Cover Optimality: 20% (reduced from 25%)
 * - Visibility + Humidity Combined: 15% (reduced from 25% total)
 * - Cloud Type Quality: 10% (increased from 5%)
 * - Atmospheric Stability (Pressure): 5% (reduced from 10%)
 *
 * @param input - Prediction input with optional V2.0 parameters
 * @param date - Optional date for seasonal ozone calculation
 * @returns Quality prediction with V2.0 scoring
 */
export function calculateQualityScoreV2(input: PredictionInput, date?: Date): QualityPrediction {
  const {
    cloudCoverTotal,
    cloudCoverLow,
    cloudCoverMid,
    cloudCoverHigh,
    humidity,
    visibility,
    pressure,
    aod550nm,
    pm25,
    ozoneDU,
  } = input;

  // ============================================
  // 1. CLOUD ALTITUDE DISTRIBUTION (30% weight)
  // ============================================
  const highCloudContribution = cloudCoverHigh * 1.0;
  const midCloudContribution = cloudCoverMid * 0.75;
  const lowCloudPenalty = cloudCoverLow * 0.5;

  let cloudAltitudeScore = highCloudContribution + midCloudContribution - lowCloudPenalty;
  cloudAltitudeScore = Math.max(0, Math.min(100, ((cloudAltitudeScore + 50) / 1.5) * 100));

  // ============================================
  // 2. AEROSOL OPTICAL DEPTH (20% weight) - NEW
  // ============================================
  let aodScore = 50; // Default neutral score if no AOD data
  if (aod550nm !== undefined && aod550nm !== null) {
    aodScore = calculateAODScore(aod550nm);
  } else if (pm25 !== undefined && pm25 !== null) {
    // Fallback: use PM2.5 if AOD unavailable
    aodScore = calculatePM25Score(pm25);
  }

  // ============================================
  // 3. TOTAL CLOUD COVER (20% weight)
  // ============================================
  const cloudCoverScore = bellCurve(cloudCoverTotal, 50, 20);

  // ============================================
  // 4. VISIBILITY + HUMIDITY COMBINED (15% weight)
  // ============================================

  // Humidity Score (7.5% of total)
  let humidityScore = 0;
  if (humidity < 30) {
    humidityScore = 100;
  } else if (humidity < 50) {
    humidityScore = 85;
  } else if (humidity < 70) {
    humidityScore = 60;
  } else if (humidity < 85) {
    humidityScore = 30;
  } else {
    humidityScore = 10;
  }

  // Visibility Score (7.5% of total)
  let visibilityScore = 0;
  const visibilityKm = visibility / 1000;

  if (visibilityKm > 20) {
    visibilityScore = 100;
  } else if (visibilityKm > 15) {
    visibilityScore = 90;
  } else if (visibilityKm > 10) {
    visibilityScore = 75;
  } else if (visibilityKm > 5) {
    visibilityScore = 50;
  } else if (visibilityKm > 2) {
    visibilityScore = 25;
  } else {
    visibilityScore = 10;
  }

  // ============================================
  // 5. CLOUD TYPE QUALITY (10% weight)
  // ============================================
  let cloudTypeBonus = 0;

  // Ideal: High cirrus clouds, minimal low clouds
  if (cloudCoverHigh > 40 && cloudCoverLow < 30) {
    cloudTypeBonus = 100;
  } else if (cloudCoverMid > 30 && cloudCoverLow < 40) {
    cloudTypeBonus = 85;
  } else if (cloudCoverHigh + cloudCoverMid > 40 && cloudCoverLow < 50) {
    cloudTypeBonus = 70;
  } else if (cloudCoverLow > 60) {
    cloudTypeBonus = 10; // Low clouds block sunset light
  } else {
    cloudTypeBonus = 50;
  }

  // ============================================
  // 6. ATMOSPHERIC STABILITY - Pressure (5% weight)
  // ============================================
  const pressureScore = gaussianCurve(pressure, 1015, 15);

  // ============================================
  // OZONE (Optional bonus - not weighted in main calculation)
  // ============================================
  let ozoneScore: number | undefined;
  if (ozoneDU !== undefined && ozoneDU !== null) {
    const month = date ? date.getMonth() : new Date().getMonth();
    ozoneScore = calculateOzoneScore(ozoneDU, month);
    // Ozone adjustment applied in final score calculation below
  }

  // ============================================
  // FINAL SCORE CALCULATION
  // ============================================
  let finalScore = Math.round(
    cloudAltitudeScore * 0.30 +
      aodScore * 0.20 +
      cloudCoverScore * 0.20 +
      humidityScore * 0.075 +
      visibilityScore * 0.075 +
      cloudTypeBonus * 0.10 +
      pressureScore * 0.05
  );

  // Apply ozone adjustment if available
  if (ozoneScore !== undefined) {
    const ozoneAdjustment = ((ozoneScore - 85) / 15) * 5;
    finalScore = Math.max(0, Math.min(100, finalScore + ozoneAdjustment));
  }

  // Category Determination
  let category: QualityCategory;
  if (finalScore >= 90) {
    category = 'VIVID';
  } else if (finalScore >= 75) {
    category = 'GREAT';
  } else if (finalScore >= 60) {
    category = 'GOOD';
  } else if (finalScore >= 40) {
    category = 'FAIR';
  } else {
    category = 'POOR';
  }

  // Enhanced Explanation
  const explanation = generateExplanationV2(
    category,
    cloudCoverHigh,
    cloudCoverMid,
    cloudCoverLow,
    cloudCoverTotal,
    humidity,
    visibilityKm,
    aod550nm,
    pm25
  );

  // Generate Factor Breakdown (V2.0)
  const factorsObject = {
    cloudAltitudeScore: Math.round(cloudAltitudeScore),
    cloudCoverScore: Math.round(cloudCoverScore),
    humidityScore: Math.round(humidityScore),
    visibilityScore: Math.round(visibilityScore),
    pressureScore: Math.round(pressureScore),
    aodScore: Math.round(aodScore),
    ozoneScore: ozoneScore ? Math.round(ozoneScore) : undefined,
    cloudTypeBonus: Math.round(cloudTypeBonus),
  };

  const factorBreakdown = generateFactorBreakdown(factorsObject, input, 'v2.0');

  return {
    score: finalScore,
    category,
    factors: factorsObject,
    explanation,
    algorithmVersion: 'v2.0',
    factorBreakdown,
  };
}

/**
 * Generate detailed factor breakdown for V2.1 algorithm (4-factor model)
 *
 * @param factors - Factor scores from V2.1 prediction calculation
 * @param input - Original prediction input for raw values
 * @returns Array of FactorExplanation objects ordered by contribution
 */
function generateFactorBreakdownV2_1(
  factors: QualityPrediction['factors'],
  input: PredictionInput,
  cloudTypeBonus: number = 0,
  ozoneAdjustment: number = 0
): FactorExplanation[] {
  const breakdown: FactorExplanation[] = [];

  // V2.1 Weights: Cloud Altitude 35%, AOD 25%, Atmospheric Clarity 25%, Stability 15%
  const weights = {
    cloudAltitude: 35,
    aod: 25,
    clarity: 25,
    stability: 15,
  };

  // 1. CLOUD ALTITUDE DISTRIBUTION (35%)
  const cloudAltitudeScore = factors.cloudAltitudeScore;
  const cloudAltitudeContribution = (cloudAltitudeScore * weights.cloudAltitude) / 100;

  let cloudAltitudeInterpretation: string;
  if (cloudAltitudeScore >= 90) {
    cloudAltitudeInterpretation = 'Excellent high cloud coverage with optimal altitude distribution';
  } else if (cloudAltitudeScore >= 70) {
    cloudAltitudeInterpretation = 'Good mid-to-high level clouds for color reflection';
  } else if (cloudAltitudeScore >= 50) {
    cloudAltitudeInterpretation = 'Moderate cloud altitude distribution';
  } else if (cloudAltitudeScore >= 30) {
    cloudAltitudeInterpretation = 'Low clouds limiting sunset color potential';
  } else {
    cloudAltitudeInterpretation = 'Poor cloud positioning for sunset viewing';
  }

  breakdown.push({
    name: 'Cloud Altitude Distribution',
    score: cloudAltitudeScore,
    weight: weights.cloudAltitude,
    contribution: cloudAltitudeContribution,
    interpretation: cloudAltitudeInterpretation,
    details: `High: ${input.cloudCoverHigh}%, Mid: ${input.cloudCoverMid}%, Low: ${input.cloudCoverLow}%, Total: ${input.cloudCoverTotal}%`,
  });

  // 2. AOD SCATTERING (25%)
  const aodScore = factors.aodScore ?? 50;
  const aodContribution = (aodScore * weights.aod) / 100;

  let aodInterpretation: string;
  let aodDetails: string;

  if (input.aod550nm !== undefined && input.aod550nm !== null) {
    const aodValue = input.aod550nm;
    if (aodValue >= 0.1 && aodValue <= 0.3) {
      aodInterpretation = 'Optimal aerosol concentration for vivid Mie scattering';
    } else if (aodValue < 0.05) {
      aodInterpretation = 'Very clean air - minimal particle scattering may reduce vibrancy';
    } else if (aodValue >= 0.05 && aodValue < 0.1) {
      aodInterpretation = 'Clean air with moderate scattering potential';
    } else if (aodValue > 0.3 && aodValue <= 0.5) {
      aodInterpretation = 'Moderate haze providing good light scattering';
    } else {
      aodInterpretation = 'High aerosol concentration may dull color intensity';
    }
    aodDetails = `AOD: ${aodValue.toFixed(3)} at 550nm (optimal: 0.1-0.3)`;
  } else {
    aodInterpretation = 'AOD estimated from visibility using Koschmieder equation';
    const estimatedAOD = estimateAODFromVisibility(input.visibility, input.humidity);
    aodDetails = `Estimated AOD: ${estimatedAOD.toFixed(3)} (from ${(input.visibility / 1000).toFixed(1)}km visibility)`;
  }

  breakdown.push({
    name: 'AOD Scattering',
    score: aodScore,
    weight: weights.aod,
    contribution: aodContribution,
    interpretation: aodInterpretation,
    details: aodDetails,
  });

  // 3. ATMOSPHERIC CLARITY (25%) - Visibility + Humidity combined
  // In V2.1, we store the combined clarity score in visibilityScore for factor breakdown
  const clarityScore = Math.round(
    (factors.visibilityScore * 0.6) + (factors.humidityScore * 0.4)
  );
  const clarityContribution = (clarityScore * weights.clarity) / 100;

  let clarityInterpretation: string;
  const visibilityKm = input.visibility / 1000;
  if (clarityScore >= 85) {
    clarityInterpretation = 'Crystal clear atmospheric conditions';
  } else if (clarityScore >= 70) {
    clarityInterpretation = 'Good atmospheric clarity for color transmission';
  } else if (clarityScore >= 50) {
    clarityInterpretation = 'Moderate atmospheric conditions';
  } else {
    clarityInterpretation = 'Poor atmospheric clarity reducing color vibrancy';
  }

  breakdown.push({
    name: 'Atmospheric Clarity',
    score: clarityScore,
    weight: weights.clarity,
    contribution: clarityContribution,
    interpretation: clarityInterpretation,
    details: `Visibility: ${visibilityKm.toFixed(1)}km (score: ${factors.visibilityScore}), Humidity: ${input.humidity}% (score: ${factors.humidityScore})`,
  });

  // 4. ATMOSPHERIC STABILITY (15%)
  const stabilityScore = factors.pressureScore;
  const stabilityContribution = (stabilityScore * weights.stability) / 100;

  let stabilityInterpretation: string;
  if (input.pressure >= 1015 && input.pressure <= 1030) {
    stabilityInterpretation = 'Optimal high pressure for stable atmospheric conditions';
  } else if (input.pressure >= 1010 && input.pressure < 1015) {
    stabilityInterpretation = 'Good atmospheric stability';
  } else if (input.pressure < 1010) {
    stabilityInterpretation = 'Lower pressure may indicate unstable conditions';
  } else {
    stabilityInterpretation = 'Moderate atmospheric stability';
  }

  breakdown.push({
    name: 'Atmospheric Stability',
    score: stabilityScore,
    weight: weights.stability,
    contribution: stabilityContribution,
    interpretation: stabilityInterpretation,
    details: `Pressure: ${input.pressure} hPa (optimal: 1015-1030 hPa)`,
  });

  // Add bonus factors when active (outside the 100% weight system)
  if (cloudTypeBonus !== 0) {
    let cirrusInterpretation: string;
    if (cloudTypeBonus > 0) {
      cirrusInterpretation = 'Cirrus clouds detected - adding bonus for enhanced light scattering';
    } else {
      cirrusInterpretation = 'Low cloud penalty applied due to stratus/nimbus coverage';
    }

    breakdown.push({
      name: 'Cloud Type Bonus',
      score: Math.abs(cloudTypeBonus) * 20, // Convert to 0-100 scale for display
      weight: 0, // Bonus factor, not part of weighted system
      contribution: cloudTypeBonus,
      interpretation: cirrusInterpretation,
      details: `Adjustment: ${cloudTypeBonus > 0 ? '+' : ''}${cloudTypeBonus} points`,
    });
  }

  if (ozoneAdjustment !== 0) {
    let ozoneInterpretation: string;
    if (ozoneAdjustment > 0) {
      ozoneInterpretation = 'Low seasonal ozone enhances warm color transmission';
    } else {
      ozoneInterpretation = 'Higher seasonal ozone slightly filters warm wavelengths';
    }

    breakdown.push({
      name: 'Ozone Adjustment',
      score: Math.abs(ozoneAdjustment) * 20, // Convert to 0-100 scale for display
      weight: 0, // Bonus factor, not part of weighted system
      contribution: ozoneAdjustment,
      interpretation: ozoneInterpretation,
      details: `Seasonal adjustment: ${ozoneAdjustment > 0 ? '+' : ''}${ozoneAdjustment} points (based on ${input.ozoneDU ?? 'estimated'} DU)`,
    });
  }

  // Sort by contribution (descending)
  breakdown.sort((a, b) => b.contribution - a.contribution);

  return breakdown;
}

/**
 * Calculate quality score based on atmospheric conditions - VERSION 2.1
 *
 * Simplified 4-factor model with enhanced weighting:
 *
 * Weight Distribution:
 * - Cloud Altitude Distribution: 35%
 * - AOD Scattering: 25% (uses Koschmieder equation fallback)
 * - Atmospheric Clarity: 25% (visibility 60% + humidity 40%)
 * - Atmospheric Stability: 15%
 *
 * Bonus Factors (outside 100%):
 * - Cloud Type (Cirrus): up to +5 points
 * - Ozone Seasonal: ±5 points
 *
 * Quality Thresholds (V2.1):
 * - VIVID: 90-100
 * - GREAT: 70-89
 * - GOOD: 50-69
 * - FAIR: 35-49
 * - POOR: 0-34
 *
 * @param input - Prediction input with atmospheric data
 * @param date - Optional date for seasonal ozone calculation
 * @returns Quality prediction with V2.1 scoring
 */
export function calculateQualityScoreV2_1(input: PredictionInput, date?: Date): QualityPrediction {
  const {
    cloudCoverTotal,
    cloudCoverLow,
    cloudCoverMid,
    cloudCoverHigh,
    humidity,
    visibility,
    pressure,
    aod550nm,
    ozoneDU,
  } = input;

  // ============================================
  // 1. CLOUD ALTITUDE DISTRIBUTION (35% weight)
  // ============================================
  // V2.1: Updated mid cloud coefficient from 0.75 to 0.7
  const highCloudScore = cloudCoverHigh * 1.0;
  const midCloudScore = cloudCoverMid * 0.7; // Changed from 0.75
  const lowCloudPenalty = cloudCoverLow * 0.5;

  // Cloud cover optimality using rangeBellCurve (30-60% optimal range)
  const cloudCoverOptimality = rangeBellCurve(cloudCoverTotal, 30, 60, 25);

  // Altitude bonus calculation
  const altitudeBonus = (highCloudScore + midCloudScore - lowCloudPenalty) / 1.5;

  // Combined cloud altitude score: 40% optimality + 60% altitude bonus
  let cloudAltitudeScore = (cloudCoverOptimality * 0.4) + (altitudeBonus * 0.6);
  cloudAltitudeScore = Math.max(0, Math.min(100, cloudAltitudeScore));

  // ============================================
  // 2. AOD SCATTERING (25% weight)
  // ============================================
  let aodScore: number;
  if (aod550nm !== undefined && aod550nm !== null) {
    // Use direct AOD measurement
    aodScore = calculateAODScore(aod550nm);
  } else {
    // Fallback: estimate AOD from visibility using Koschmieder equation
    const estimatedAOD = estimateAODFromVisibility(visibility, humidity);
    aodScore = calculateAODScore(estimatedAOD);
  }

  // ============================================
  // 3. ATMOSPHERIC CLARITY (25% weight)
  // ============================================
  const visibilityKm = visibility / 1000;
  const visibilityScore = Math.min(100, visibilityKm * 5);
  const humidityScore = rangeBellCurve(humidity, 30, 60, 25);
  const clarityScore = (visibilityScore * 0.6) + (humidityScore * 0.4);

  // ============================================
  // 4. ATMOSPHERIC STABILITY (15% weight)
  // ============================================
  const stabilityScore = rangeBellCurve(pressure, 1015, 1030, 15);

  // ============================================
  // BONUS FACTORS (Outside 100%)
  // ============================================

  // Cloud Type Bonus: up to +5 points for cirrus dominance
  let cloudTypeBonus = 0;
  if (cloudCoverHigh > 40 && cloudCoverLow < 30) {
    // High cirrus with minimal low clouds - maximum bonus
    cloudTypeBonus = 5;
  } else if (cloudCoverHigh > 30 && cloudCoverLow < 40) {
    cloudTypeBonus = 3;
  } else if (cloudCoverHigh > 20 && cloudCoverLow < 50) {
    cloudTypeBonus = 1;
  }

  // Ozone Seasonal Adjustment: ±5 points
  let ozoneScore: number | undefined;
  let ozoneAdjustment = 0;
  if (ozoneDU !== undefined && ozoneDU !== null) {
    const month = date ? date.getMonth() : new Date().getMonth();
    ozoneScore = calculateOzoneScore(ozoneDU, month);
    // Scale from -5 to +5 based on ozone score (70-100 range)
    ozoneAdjustment = ((ozoneScore - 85) / 15) * 5;
  }

  // ============================================
  // FINAL SCORE CALCULATION
  // ============================================
  let finalScore = Math.round(
    cloudAltitudeScore * 0.35 +
    aodScore * 0.25 +
    clarityScore * 0.25 +
    stabilityScore * 0.15
  );

  // Apply bonus factors
  finalScore += cloudTypeBonus;
  finalScore += ozoneAdjustment;

  // Clamp to 0-100 range
  finalScore = Math.max(0, Math.min(100, Math.round(finalScore)));

  // ============================================
  // CATEGORY DETERMINATION (V2.1 Thresholds)
  // ============================================
  let category: QualityCategory;
  if (finalScore >= 90) {
    category = 'VIVID';
  } else if (finalScore >= 70) { // Changed from 75
    category = 'GREAT';
  } else if (finalScore >= 50) { // Changed from 60
    category = 'GOOD';
  } else if (finalScore >= 35) { // Changed from 40
    category = 'FAIR';
  } else {
    category = 'POOR';
  }

  // ============================================
  // EXPLANATION GENERATION
  // ============================================
  const explanation = generateExplanationV2_1(
    category,
    cloudCoverHigh,
    cloudCoverMid,
    cloudCoverLow,
    cloudCoverTotal,
    humidity,
    visibilityKm,
    aod550nm,
    cloudTypeBonus,
    ozoneAdjustment
  );

  // ============================================
  // BUILD FACTORS OBJECT
  // ============================================
  const factorsObject = {
    cloudAltitudeScore: Math.round(cloudAltitudeScore),
    cloudCoverScore: Math.round(cloudCoverOptimality), // Store optimality for reference
    humidityScore: Math.round(humidityScore),
    visibilityScore: Math.round(visibilityScore),
    pressureScore: Math.round(stabilityScore),
    aodScore: Math.round(aodScore),
    ozoneScore: ozoneScore ? Math.round(ozoneScore) : undefined,
    cloudTypeBonus: Math.round(cloudTypeBonus),
  };

  const factorBreakdown = generateFactorBreakdownV2_1(factorsObject, input, cloudTypeBonus, ozoneAdjustment);

  return {
    score: finalScore,
    category,
    factors: factorsObject,
    explanation,
    algorithmVersion: 'v2.1',
    factorBreakdown,
  };
}

/**
 * Generate human-readable explanation - VERSION 2.1
 * Enhanced with 4-factor model context and bonus factors
 */
function generateExplanationV2_1(
  category: QualityCategory,
  highCloud: number,
  midCloud: number,
  lowCloud: number,
  totalCloud: number,
  humidity: number,
  visibilityKm: number,
  aod550nm?: number,
  cloudTypeBonus?: number,
  ozoneAdjustment?: number
): string {
  const parts: string[] = [];

  // Cloud altitude analysis
  if (highCloud > 40) {
    parts.push('High cirrus clouds will create spectacular reflections lasting 20-30 minutes after sunset');
  } else if (midCloud > 30) {
    parts.push('Mid-level clouds will provide good color reflections for 15-20 minutes');
  } else if (lowCloud > 60) {
    parts.push('Low clouds may block the horizon and limit color development');
  }

  // Cloud cover optimality (V2.1: 30-60% optimal range)
  if (totalCloud >= 30 && totalCloud <= 60) {
    parts.push('optimal cloud coverage for vibrant colors');
  } else if (totalCloud < 30) {
    parts.push('limited cloud cover may result in less dramatic colors');
  } else if (totalCloud > 60) {
    parts.push('heavy cloud cover may reduce color intensity');
  }

  // AOD/scattering analysis
  if (aod550nm !== undefined) {
    if (aod550nm >= 0.1 && aod550nm <= 0.3) {
      parts.push('ideal aerosol levels will enhance red and orange hues through optimal Mie scattering');
    } else if (aod550nm < 0.1) {
      parts.push('very clean air may produce less vivid colors due to minimal particle scattering');
    } else if (aod550nm > 0.5) {
      parts.push('high aerosol concentrations may dull color vibrancy');
    }
  }

  // Atmospheric clarity
  if (humidity >= 30 && humidity <= 60 && visibilityKm > 10) {
    parts.push('excellent atmospheric clarity will enhance color transmission');
  } else if (humidity > 70 || visibilityKm < 5) {
    parts.push('humid or hazy conditions may reduce color intensity');
  }

  // Bonus factor mentions
  if (cloudTypeBonus && cloudTypeBonus >= 3) {
    parts.push('cirrus cloud formations add extra vibrancy potential');
  }
  if (ozoneAdjustment && Math.abs(ozoneAdjustment) >= 3) {
    if (ozoneAdjustment > 0) {
      parts.push('seasonal ozone levels favor enhanced color balance');
    } else {
      parts.push('ozone levels may slightly affect color balance');
    }
  }

  // Photography tips
  if (category === 'VIVID' || category === 'GREAT') {
    parts.push('Arrive 30 minutes before sunset and stay 30 minutes after for peak colors');
  } else if (category === 'GOOD') {
    parts.push('Colors should develop nicely - best viewing 15-20 minutes around sunset');
  }

  return parts.join('. ') + '.';
}

/**
 * Calculate quality score based on atmospheric conditions - VERSION 2.2
 *
 * Enhanced 4-factor model with improved empirical calibration:
 *
 * Weight Distribution:
 * - Cloud Altitude Distribution: 35%
 * - AOD Scattering: 25% (tighter sweet spot 0.12-0.25, peaks at 0.18)
 * - Atmospheric Clarity: 25% (visibility 60% + humidity 40%)
 * - Atmospheric Stability: 15%
 *
 * V2.2 Improvements over V2.1:
 * - Humidity: exponential decay favoring dry air
 *   (RH 25%→100, 30%→~88, 50%→~54, 70%→~33, 90%→~20)
 * - Post-rain bonus: +5 only when precipitationLast6hMm ≥ 0.5
 * - Tighter AOD sweet spot: 0.12-0.25 (vs 0.1-0.3 in V2.1)
 * - When AOD is estimated from visibility (Koschmieder), clarity is
 *   humidity-dominant so visibility is not double-counted
 *
 * Bonus Factors (outside 100%):
 * - Cloud Type (Cirrus): up to +5 points
 * - Ozone Seasonal: ±5 points
 * - Post-Rain Bonus: +5 points (requires precip input)
 *
 * Quality Thresholds (same as V2.1):
 * - VIVID: 90-100
 * - GREAT: 70-89
 * - GOOD: 50-69
 * - FAIR: 35-49
 * - POOR: 0-34
 *
 * @param input - Prediction input with atmospheric data
 * @param date - Optional date for seasonal ozone calculation
 * @returns Quality prediction with V2.2 scoring
 */
export function calculateQualityScoreV2_2(input: PredictionInput, date?: Date): QualityPrediction {
  const {
    cloudCoverTotal,
    cloudCoverLow,
    cloudCoverMid,
    cloudCoverHigh,
    humidity,
    visibility,
    pressure,
    aod550nm,
    ozoneDU,
    precipitationLast6hMm,
  } = input;

  // ============================================
  // 1. CLOUD ALTITUDE DISTRIBUTION (35% weight)
  // ============================================
  const highCloudScore = cloudCoverHigh * 1.0;
  const midCloudScore = cloudCoverMid * 0.7;
  const lowCloudPenalty = cloudCoverLow * 0.5;

  // Cloud cover optimality using rangeBellCurve (30-60% optimal range)
  const cloudCoverOptimality = rangeBellCurve(cloudCoverTotal, 30, 60, 25);

  // Altitude bonus calculation
  const altitudeBonus = (highCloudScore + midCloudScore - lowCloudPenalty) / 1.5;

  // Combined cloud altitude score: 40% optimality + 60% altitude bonus
  let cloudAltitudeScore = (cloudCoverOptimality * 0.4) + (altitudeBonus * 0.6);
  cloudAltitudeScore = Math.max(0, Math.min(100, cloudAltitudeScore));

  // ============================================
  // 2. AOD SCATTERING (25% weight) - V2.2 tighter sweet spot
  // ============================================
  const hasDirectAod = aod550nm !== undefined && aod550nm !== null;
  let aodScore: number;
  if (hasDirectAod) {
    // Use V2.2 AOD function with tighter sweet spot (0.12-0.25)
    aodScore = calculateAODScoreV2_2(aod550nm as number);
  } else {
    // Fallback: estimate AOD from visibility using Koschmieder equation
    // (horizontal met range → rough vertical AOD proxy; imperfect physics)
    const estimatedAOD = estimateAODFromVisibility(visibility, humidity);
    aodScore = calculateAODScoreV2_2(estimatedAOD);
  }

  // ============================================
  // 3. ATMOSPHERIC CLARITY (25% weight)
  // ============================================
  const visibilityKm = visibility / 1000;
  const visibilityScore = Math.min(100, visibilityKm * 5);

  // V2.2: Exponential decay favoring dry air
  // RH 25% → 100, 30% → ~88, 50% → ~54, 70% → ~33, 90% → ~20
  const humidityScore = Math.max(5, 100 * Math.exp(-0.025 * Math.max(0, humidity - 25)));

  // Avoid double-counting visibility when it already drove AOD estimation:
  // - Direct AOD: visibility + humidity both informative (0.6 / 0.4)
  // - Estimated AOD: humidity-dominant clarity (0.25 / 0.75)
  const clarityScore = hasDirectAod
    ? visibilityScore * 0.6 + humidityScore * 0.4
    : visibilityScore * 0.25 + humidityScore * 0.75;

  // ============================================
  // 4. ATMOSPHERIC STABILITY (15% weight)
  // ============================================
  const stabilityScore = rangeBellCurve(pressure, 1015, 1030, 15);

  // ============================================
  // BONUS FACTORS (Outside 100%)
  // ============================================

  // Cloud Type Bonus: up to +5 points for cirrus dominance
  let cloudTypeBonus = 0;
  if (cloudCoverHigh > 40 && cloudCoverLow < 30) {
    cloudTypeBonus = 5;
  } else if (cloudCoverHigh > 30 && cloudCoverLow < 40) {
    cloudTypeBonus = 3;
  } else if (cloudCoverHigh > 20 && cloudCoverLow < 50) {
    cloudTypeBonus = 1;
  }

  // Ozone Seasonal Adjustment: ±5 points
  let ozoneScore: number | undefined;
  let ozoneAdjustment = 0;
  if (ozoneDU !== undefined && ozoneDU !== null) {
    const month = date ? date.getMonth() : new Date().getMonth();
    ozoneScore = calculateOzoneScore(ozoneDU, month);
    ozoneAdjustment = ((ozoneScore - 85) / 15) * 5;
  }

  // Post-rain bonus: only with measured recent precipitation (washed atmosphere).
  // Requires precip ≥ 0.5 mm in last ~6h plus supportive clear-air conditions.
  let postRainBonus = 0;
  const hasRecentPrecip =
    precipitationLast6hMm !== undefined &&
    precipitationLast6hMm !== null &&
    precipitationLast6hMm >= 0.5;
  if (
    hasRecentPrecip &&
    visibilityKm > 12 &&
    humidity >= 40 &&
    humidity <= 80 &&
    pressure >= 1008 &&
    pressure <= 1028
  ) {
    postRainBonus = 5;
  }

  // ============================================
  // FINAL SCORE CALCULATION
  // ============================================
  let finalScore = Math.round(
    cloudAltitudeScore * 0.35 +
    aodScore * 0.25 +
    clarityScore * 0.25 +
    stabilityScore * 0.15
  );

  // Apply bonus factors
  finalScore += cloudTypeBonus;
  finalScore += ozoneAdjustment;
  finalScore += postRainBonus;

  // Clamp to 0-100 range
  finalScore = Math.max(0, Math.min(100, Math.round(finalScore)));

  // ============================================
  // CATEGORY DETERMINATION (V2.2 uses V2.1 thresholds)
  // ============================================
  let category: QualityCategory;
  if (finalScore >= 90) {
    category = 'VIVID';
  } else if (finalScore >= 70) {
    category = 'GREAT';
  } else if (finalScore >= 50) {
    category = 'GOOD';
  } else if (finalScore >= 35) {
    category = 'FAIR';
  } else {
    category = 'POOR';
  }

  // ============================================
  // EXPLANATION GENERATION
  // ============================================
  const explanation = generateExplanationV2_2(
    category,
    cloudCoverHigh,
    cloudCoverMid,
    cloudCoverLow,
    cloudCoverTotal,
    humidity,
    visibilityKm,
    aod550nm,
    cloudTypeBonus,
    ozoneAdjustment,
    postRainBonus
  );

  // ============================================
  // BUILD FACTORS OBJECT
  // ============================================
  const factorsObject = {
    cloudAltitudeScore: Math.round(cloudAltitudeScore),
    cloudCoverScore: Math.round(cloudCoverOptimality),
    humidityScore: Math.round(humidityScore),
    visibilityScore: Math.round(visibilityScore),
    pressureScore: Math.round(stabilityScore),
    aodScore: Math.round(aodScore),
    ozoneScore: ozoneScore ? Math.round(ozoneScore) : undefined,
    cloudTypeBonus: Math.round(cloudTypeBonus),
  };

  const factorBreakdown = generateFactorBreakdownV2_2(
    factorsObject,
    input,
    cloudTypeBonus,
    ozoneAdjustment,
    postRainBonus
  );

  return {
    score: finalScore,
    category,
    factors: factorsObject,
    explanation,
    algorithmVersion: 'v2.2' as AlgorithmVersion,
    factorBreakdown,
  };
}

/**
 * Generate human-readable explanation - VERSION 2.2
 * Enhanced with post-rain bonus and improved humidity context
 */
function generateExplanationV2_2(
  category: QualityCategory,
  highCloud: number,
  midCloud: number,
  lowCloud: number,
  totalCloud: number,
  humidity: number,
  visibilityKm: number,
  aod550nm?: number,
  cloudTypeBonus?: number,
  ozoneAdjustment?: number,
  postRainBonus?: number
): string {
  const parts: string[] = [];

  // Cloud altitude analysis
  if (highCloud > 40) {
    parts.push('High cirrus clouds will create spectacular reflections lasting 20-30 minutes after sunset');
  } else if (midCloud > 30) {
    parts.push('Mid-level clouds will provide good color reflections for 15-20 minutes');
  } else if (lowCloud > 60) {
    parts.push('Low clouds may block the horizon and limit color development');
  }

  // Cloud cover optimality (V2.2: 30-60% optimal range)
  if (totalCloud >= 30 && totalCloud <= 60) {
    parts.push('optimal cloud coverage for vibrant colors');
  } else if (totalCloud < 30) {
    parts.push('limited cloud cover may result in less dramatic colors');
  } else if (totalCloud > 60) {
    parts.push('heavy cloud cover may reduce color intensity');
  }

  // AOD/scattering analysis
  if (aod550nm !== undefined) {
    if (aod550nm >= 0.12 && aod550nm <= 0.25) {
      parts.push('ideal aerosol levels will enhance red and orange hues through optimal Mie scattering');
    } else if (aod550nm < 0.12) {
      parts.push('very clean air may produce less vivid colors due to minimal particle scattering');
    } else if (aod550nm > 0.4) {
      parts.push('high aerosol concentrations may dull color vibrancy');
    }
  }

  // Atmospheric clarity with improved humidity context
  if (humidity < 40 && visibilityKm > 10) {
    parts.push('dry air and excellent clarity will maximize color vibrancy');
  } else if (humidity >= 40 && humidity <= 60 && visibilityKm > 15) {
    parts.push('good atmospheric clarity with balanced humidity');
  } else if (humidity > 70 || visibilityKm < 5) {
    parts.push('humid or hazy conditions may reduce color intensity');
  }

  // Post-rain bonus mention
  if (postRainBonus && postRainBonus > 0) {
    parts.push('post-rain atmospheric conditions detected - washed atmosphere may produce exceptional colors');
  }

  // Bonus factor mentions
  if (cloudTypeBonus && cloudTypeBonus >= 3) {
    parts.push('cirrus cloud formations add extra vibrancy potential');
  }
  if (ozoneAdjustment && Math.abs(ozoneAdjustment) >= 3) {
    if (ozoneAdjustment > 0) {
      parts.push('seasonal ozone levels favor enhanced color balance');
    } else {
      parts.push('ozone levels may slightly affect color balance');
    }
  }

  // Photography tips
  if (category === 'VIVID' || category === 'GREAT') {
    parts.push('Arrive 30 minutes before sunset and stay 30 minutes after for peak colors');
  } else if (category === 'GOOD') {
    parts.push('Colors should develop nicely - best viewing 15-20 minutes around sunset');
  }

  return parts.join('. ') + '.';
}

/**
 * Generate detailed factor breakdown for V2.2 algorithm
 */
function generateFactorBreakdownV2_2(
  factors: QualityPrediction['factors'],
  input: PredictionInput,
  cloudTypeBonus: number = 0,
  ozoneAdjustment: number = 0,
  postRainBonus: number = 0
): FactorExplanation[] {
  // Use V2.1 breakdown as base
  const breakdown = generateFactorBreakdownV2_1(factors, input, cloudTypeBonus, ozoneAdjustment);

  // Add post-rain bonus if present
  if (postRainBonus > 0) {
    const visibilityKm = input.visibility / 1000;
    breakdown.push({
      name: 'Post-Rain Bonus',
      score: 100,
      weight: 0, // Bonus factor, not part of weighted system
      contribution: postRainBonus,
      interpretation: 'Washed atmosphere after rain detected - ideal conditions for exceptional colors',
      details: `High visibility (${visibilityKm.toFixed(1)}km), moderate humidity (${input.humidity}%), rising pressure (${input.pressure} hPa)`,
    });
  }

  // Re-sort by contribution
  breakdown.sort((a, b) => b.contribution - a.contribution);

  return breakdown;
}

/**
 * Default export uses V2.2 algorithm
 * Call V1, V2.0, or V2.1 explicitly if needed for comparison
 *
 * @param input - Prediction input with atmospheric data
 * @param date - Optional date for seasonal calculations
 * @param version - Algorithm version to use (defaults to 'v2.2')
 * @returns Quality prediction with specified algorithm version
 */
export function calculateQualityScore(
  input: PredictionInput,
  date?: Date,
  version: AlgorithmVersion = 'v2.2'
): QualityPrediction {
  switch (version) {
    case 'v1.0':
      return calculateQualityScoreV1(input);
    case 'v2.0':
      return calculateQualityScoreV2(input, date);
    case 'v2.1':
      return calculateQualityScoreV2_1(input, date);
    case 'v2.2':
    default:
      return calculateQualityScoreV2_2(input, date);
  }
}

/**
 * Generate human-readable explanation
 */
function generateExplanation(
  category: QualityCategory,
  highCloud: number,
  midCloud: number,
  lowCloud: number,
  totalCloud: number,
  humidity: number,
  visibilityKm: number
): string {
  const parts: string[] = [];

  if (highCloud > 40) {
    parts.push('High cirrus clouds will create spectacular reflections lasting 20-30 minutes after sunset');
  } else if (midCloud > 30) {
    parts.push('Mid-level clouds will provide good color reflections for 15-20 minutes');
  } else if (lowCloud > 60) {
    parts.push('Low clouds may block the horizon and limit color development');
  }

  if (totalCloud >= 40 && totalCloud <= 60) {
    parts.push('optimal cloud coverage for vibrant colors');
  } else if (totalCloud < 30) {
    parts.push('limited cloud cover may result in less dramatic colors');
  } else if (totalCloud > 70) {
    parts.push('heavy cloud cover may flatten the color palette');
  }

  if (humidity < 40 && visibilityKm > 15) {
    parts.push('excellent atmospheric clarity will enhance color vibrancy');
  } else if (humidity > 70 || visibilityKm < 5) {
    parts.push('humid or hazy conditions may reduce color intensity');
  }

  if (category === 'VIVID' || category === 'GREAT') {
    parts.push('Arrive 30 minutes before sunset and stay 30 minutes after for peak colors');
  }

  return parts.join('. ') + '.';
}

/**
 * Generate human-readable explanation - VERSION 2.0
 * Enhanced with AOD and particulate matter context
 */
function generateExplanationV2(
  category: QualityCategory,
  highCloud: number,
  midCloud: number,
  lowCloud: number,
  totalCloud: number,
  humidity: number,
  visibilityKm: number,
  aod550nm?: number,
  pm25?: number
): string {
  const parts: string[] = [];

  // Cloud analysis
  if (highCloud > 40) {
    parts.push('High cirrus clouds will create spectacular reflections lasting 20-30 minutes after sunset');
  } else if (midCloud > 30) {
    parts.push('Mid-level clouds will provide good color reflections for 15-20 minutes');
  } else if (lowCloud > 60) {
    parts.push('Low clouds may block the horizon and limit color development');
  }

  if (totalCloud >= 40 && totalCloud <= 60) {
    parts.push('optimal cloud coverage for vibrant colors');
  } else if (totalCloud < 30) {
    parts.push('limited cloud cover may result in less dramatic colors');
  } else if (totalCloud > 70) {
    parts.push('heavy cloud cover may flatten the color palette');
  }

  // AOD analysis (NEW)
  if (aod550nm !== undefined) {
    if (aod550nm >= 0.1 && aod550nm <= 0.3) {
      parts.push('ideal aerosol levels will enhance red and orange hues through optimal light scattering');
    } else if (aod550nm < 0.1) {
      parts.push('very clean air may produce less vivid colors due to minimal particle scattering');
    } else if (aod550nm > 0.5) {
      parts.push('high aerosol concentrations may dull color vibrancy');
    }
  }

  // PM2.5 analysis (NEW - alternative to AOD)
  if (pm25 !== undefined && aod550nm === undefined) {
    if (pm25 <= 35) {
      parts.push('good air quality supports excellent color development');
    } else if (pm25 > 75) {
      parts.push('elevated particulate matter may reduce clarity and color intensity');
    }
  }

  // Atmospheric clarity
  if (humidity < 40 && visibilityKm > 15) {
    parts.push('excellent atmospheric clarity will enhance color vibrancy');
  } else if (humidity > 70 || visibilityKm < 5) {
    parts.push('humid or hazy conditions may reduce color intensity');
  }

  // Photography tips
  if (category === 'VIVID' || category === 'GREAT') {
    parts.push('Arrive 30 minutes before sunset and stay 30 minutes after for peak colors');
  }

  return parts.join('. ') + '.';
}

/**
 * Calculate Haversine distance between two coordinates in km
 */
export function calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371; // Earth's radius in km
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
